import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  AlertTriangle, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle,
  Loader2,
  AlertCircle,
  Wallet,
  ShieldAlert,
  RefreshCw,
  X,
  CreditCard,
  User,
  Calendar,
  FileText,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const UserDisputesSection = () => {
  const [disputes, setDisputes] = useState([]);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDispute, setSelectedDispute] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [disputesRes, payoutRes] = await Promise.all([
        apiClient.get('/disputes/my-disputes'),
        apiClient.get('/disputes/payout-status')
      ]);
      
      setDisputes(disputesRes.data.disputes || []);
      setPayoutStatus(payoutRes.data);
    } catch (err) {
      console.error('Error fetching disputes:', err);
      setError('Failed to load dispute information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'open': { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
      'pending': { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
      'under_review': { color: 'bg-blue-500/20 text-blue-400', icon: AlertCircle },
      'needs_response': { color: 'bg-red-500/20 text-red-400', icon: AlertTriangle },
      'won': { color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
      'lost': { color: 'bg-red-500/20 text-red-400', icon: XCircle },
    };
    
    const config = statusConfig[status] || { color: 'bg-slate-500/20 text-muted-foreground', icon: AlertCircle };
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-disputes-section">
      {/* Payout Status Card */}
      {payoutStatus && (
        <Card className={`border-2 ${payoutStatus.payout_frozen || payoutStatus.open_disputes > 0 ? 'border-red-500/50 bg-red-500/5' : 'border-green-500/50 bg-green-500/5'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Wallet className="w-5 h-5" />
              Payout Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Can Payout */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-card">
                {payoutStatus.can_payout ? (
                  <CheckCircle className="w-8 h-8 text-green-500" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-500" />
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Payout Eligibility</p>
                  <p className={`font-semibold ${payoutStatus.can_payout ? 'text-green-500' : 'text-red-500'}`}>
                    {payoutStatus.can_payout ? 'Eligible' : 'On Hold'}
                  </p>
                </div>
              </div>
              
              {/* Open Disputes */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-card">
                <ShieldAlert className={`w-8 h-8 ${payoutStatus.open_disputes > 0 ? 'text-yellow-500' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-sm text-muted-foreground">Open Disputes</p>
                  <p className={`font-semibold ${payoutStatus.open_disputes > 0 ? 'text-yellow-500' : 'text-foreground'}`}>
                    {payoutStatus.open_disputes}
                  </p>
                </div>
              </div>
              
              {/* Held Amount */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-card">
                <DollarSign className={`w-8 h-8 ${payoutStatus.held_amount > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-sm text-muted-foreground">Amount On Hold</p>
                  <p className={`font-semibold ${payoutStatus.held_amount > 0 ? 'text-red-500' : 'text-foreground'}`}>
                    {formatCurrency(payoutStatus.held_amount)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Frozen Reason */}
            {payoutStatus.payout_frozen && payoutStatus.frozen_reason && (
              <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-500">Payout On Hold due to Dispute</p>
                    <p className="text-sm text-muted-foreground mt-1">{payoutStatus.frozen_reason}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Disputes List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Disputes & Chargebacks
            {disputes.length > 0 && (
              <Badge variant="outline" className="ml-2">{disputes.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {disputes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50 text-green-500" />
              <p>No disputes or chargebacks found.</p>
              <p className="text-sm mt-1">That's great news!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {disputes.map((dispute) => (
                <div 
                  key={dispute.id} 
                  className="p-4 rounded-lg border border-border bg-card/50 hover:bg-card transition-colors cursor-pointer"
                  data-testid={`dispute-item-${dispute.id}`}
                  onClick={() => setSelectedDispute(dispute)}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {getStatusBadge(dispute.status)}
                        <Badge variant="outline" className="text-xs capitalize">{dispute.type || 'chargeback'}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(dispute.created_at)}
                        </span>
                      </div>
                      <p className="font-medium mt-2 text-sm">
                        Dispute ID: <span className="font-mono text-xs">{dispute.id}</span>
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Reason: <span className="capitalize">{(dispute.reason || '').replace(/_/g, ' ')}</span>
                      </p>
                      {dispute.donor_name && (
                        <p className="text-sm text-muted-foreground">
                          Donor: {dispute.donor_name}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-red-500">
                          {formatCurrency(dispute.amount)}
                        </p>
                        <p className="text-xs text-muted-foreground uppercase">{dispute.currency || 'usd'}</p>
                        {dispute.evidence_due_by && dispute.status !== 'won' && dispute.status !== 'lost' && (
                          <p className="text-xs text-yellow-500 mt-1">
                            Evidence due: {formatDate(dispute.evidence_due_by)}
                          </p>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dispute Detail Modal */}
      {selectedDispute && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelectedDispute(null)}>
          <div 
            className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto m-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="dispute-detail-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Dispute Details</h3>
                <p className="text-xs text-muted-foreground font-mono">{selectedDispute.id}</p>
              </div>
              <button onClick={() => setSelectedDispute(null)} className="p-2 rounded-lg hover:bg-secondary transition-colors">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status & Amount Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getStatusBadge(selectedDispute.status)}
                  <Badge variant="outline" className="capitalize">{selectedDispute.type || 'chargeback'}</Badge>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-red-500">{formatCurrency(selectedDispute.amount)}</p>
                  <p className="text-xs text-muted-foreground uppercase">{selectedDispute.currency || 'USD'}</p>
                </div>
              </div>

              {/* Overview Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3" /> Dispute Reason</p>
                    <p className="font-medium text-foreground capitalize mt-1">{(selectedDispute.reason || 'Unknown').replace(/_/g, ' ')}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><CreditCard className="w-3 h-3" /> Payment Reference</p>
                    <p className="font-mono text-xs text-foreground mt-1">{selectedDispute.payment_intent_id || 'N/A'}</p>
                  </div>
                  {selectedDispute.stripe_dispute_id && (
                    <div className="p-3 rounded-lg bg-secondary/50">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Stripe Dispute ID</p>
                      <p className="font-mono text-xs text-foreground mt-1">{selectedDispute.stripe_dispute_id}</p>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {selectedDispute.donor_name && (
                    <div className="p-3 rounded-lg bg-secondary/50">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> Donor</p>
                      <p className="font-medium text-foreground mt-1">{selectedDispute.donor_name}</p>
                      {selectedDispute.donor_email && (
                        <p className="text-xs text-muted-foreground">{selectedDispute.donor_email}</p>
                      )}
                    </div>
                  )}
                  {selectedDispute.campaign_id && (
                    <div className="p-3 rounded-lg bg-secondary/50">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3" /> Campaign</p>
                      <a href={`/campaign/${selectedDispute.campaign_id}`} className="text-sm text-emerald-400 hover:underline mt-1 block font-mono">
                        {selectedDispute.campaign_id}
                      </a>
                    </div>
                  )}
                  {selectedDispute.evidence_due_by && selectedDispute.status !== 'won' && selectedDispute.status !== 'lost' && (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                      <p className="text-xs text-yellow-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Evidence Due By</p>
                      <p className="font-medium text-yellow-400 mt-1">{formatDate(selectedDispute.evidence_due_by)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  Timeline
                </h4>
                <div className="space-y-3 pl-4 border-l-2 border-border">
                  <div className="relative">
                    <div className="absolute -left-[21px] w-3 h-3 rounded-full bg-red-500 border-2 border-card" />
                    <p className="text-sm text-foreground">Dispute opened</p>
                    <p className="text-xs text-muted-foreground">{new Date(selectedDispute.created_at).toLocaleString()}</p>
                  </div>
                  {selectedDispute.updated_at && selectedDispute.updated_at !== selectedDispute.created_at && (
                    <div className="relative">
                      <div className="absolute -left-[21px] w-3 h-3 rounded-full bg-blue-500 border-2 border-card" />
                      <p className="text-sm text-foreground">Last updated</p>
                      <p className="text-xs text-muted-foreground">{new Date(selectedDispute.updated_at).toLocaleString()}</p>
                    </div>
                  )}
                  {(selectedDispute.status === 'won' || selectedDispute.status === 'lost') && (
                    <div className="relative">
                      <div className={`absolute -left-[21px] w-3 h-3 rounded-full border-2 border-card ${selectedDispute.status === 'won' ? 'bg-green-500' : 'bg-red-500'}`} />
                      <p className="text-sm text-foreground">Dispute {selectedDispute.status}</p>
                      <p className="text-xs text-muted-foreground">{new Date(selectedDispute.updated_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Status explanation */}
              <div className="p-4 rounded-lg bg-secondary/30 border border-border">
                <p className="text-sm font-medium text-foreground mb-1">What does "{selectedDispute.status.replace(/_/g, ' ')}" mean?</p>
                <p className="text-xs text-muted-foreground">
                  {selectedDispute.status === 'needs_response' && 'Evidence needs to be submitted to the card issuer to contest this dispute. Funds are on hold until resolved.'}
                  {selectedDispute.status === 'under_review' && 'Evidence has been submitted and is being reviewed by the card issuer. This typically takes 60-90 days.'}
                  {selectedDispute.status === 'open' && 'This dispute is currently open and being processed.'}
                  {selectedDispute.status === 'pending' && 'This dispute is pending review.'}
                  {selectedDispute.status === 'won' && 'This dispute was resolved in your favor. Funds have been released.'}
                  {selectedDispute.status === 'lost' && 'This dispute was resolved in the donor\'s favor. The disputed amount has been deducted.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Info Card */}
      <Card className="bg-blue-500/5 border-blue-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">About Disputes</p>
              <p>
                Disputes (chargebacks) occur when a donor contacts their bank to reverse a transaction. 
                During an active dispute, affected funds are held and payouts may be temporarily frozen.
                Most disputes are resolved within 60-90 days.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserDisputesSection;
